import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  fetchBonuses, createBonus, updateBonus,
  approveBonusByManager, approveBonusByHR, approveBonusByFinance, approveBonusByGM, markBonusPaid, cancelBonus
} from '../../../../services/salary';
import { fetchEmployees } from '../../../../services/employee';
import CustomPagination from '../../../../components/common/CustomPagination.js';

const Bonuses = () => {
  const { t } = useTranslation(['common', 'employees', 'salary']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    employee: '',
    bonus_type: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    employee: '',
    bonus_type: 'performance',
    amount: '',
    currency: 'NGN',
    payment_date: '',
    description: '',
    is_taxable: true
  });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, bonusId: null });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchBonuses(params);
      
      if (response.results) {
        setBonuses(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setBonuses(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch bonuses');
      setBonuses([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchEmployeesData = async () => {
      if (modalOpen) {
        try {
          const response = await fetchEmployees({ page_size: 1000 });
          setEmployees(response.results || response || []);
        } catch (err) {
          console.error('Failed to fetch employees:', err);
        }
      }
    };
    fetchEmployeesData();
  }, [modalOpen]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (bonus = null) => {
    if (bonus) {
      setEditId(bonus.id);
      setForm({
        employee: bonus.employee?.id || bonus.employee || '',
        bonus_type: bonus.bonus_type || 'performance',
        amount: bonus.amount || '',
        currency: bonus.currency || 'NGN',
        payment_date: bonus.payment_date || '',
        description: bonus.description || '',
        is_taxable: bonus.is_taxable !== undefined ? bonus.is_taxable : true
      });
    } else {
      setEditId(null);
      setForm({
        employee: '',
        bonus_type: 'performance',
        amount: '',
        currency: 'NGN',
        payment_date: '',
        description: '',
        is_taxable: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      employee: '',
      bonus_type: 'performance',
      amount: '',
      currency: 'NGN',
      payment_date: '',
      description: '',
      is_taxable: true
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.employee) {
      setSnackbar({ open: true, message: 'Employee is required', severity: 'error' });
      return;
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      setSnackbar({ open: true, message: 'Amount is required and must be greater than 0', severity: 'error' });
      return;
    }

    if (!form.payment_date) {
      setSnackbar({ open: true, message: 'Payment date is required', severity: 'error' });
      return;
    }

    if (!form.description || !form.description.trim()) {
      setSnackbar({ open: true, message: 'Description is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        employee: form.employee,
        bonus_type: form.bonus_type,
        amount: form.amount,
        currency: form.currency,
        payment_date: form.payment_date,
        description: form.description.trim(),
        is_taxable: form.is_taxable
      };

      if (editId) {
        await updateBonus(editId, payload);
        setSnackbar({ open: true, message: 'Bonus updated successfully', severity: 'success' });
      } else {
        await createBonus(payload);
        setSnackbar({ open: true, message: 'Bonus created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save bonus';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleActionMenuOpen = (event, bonusId) => {
    setActionMenu({ anchorEl: event.currentTarget, bonusId });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, bonusId: null });
  };

  const handleApprove = async (action) => {
    const bonusId = actionMenu.bonusId;
    handleActionMenuClose();

    try {
      let result;
      switch (action) {
        case 'manager':
          result = await approveBonusByManager(bonusId);
          break;
        case 'hr':
          result = await approveBonusByHR(bonusId);
          break;
        case 'finance':
          result = await approveBonusByFinance(bonusId);
          break;
        case 'gm':
          result = await approveBonusByGM(bonusId);
          break;
        case 'paid':
          result = await markBonusPaid(bonusId);
          break;
        case 'cancel':
          if (!window.confirm('Are you sure you want to cancel this bonus?')) {
            return;
          }
          result = await cancelBonus(bonusId);
          break;
        default:
          return;
      }
      setSnackbar({ open: true, message: 'Action completed successfully', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || err.message || 'Failed to perform action', severity: 'error' });
    }
  };

  const rowsWithSerial = useMemo(() => {
    return bonuses.map((item, index) => ({
      ...item,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [bonuses, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('salary:bonuses.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('salary:bonuses.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('salary:bonuses.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'bonus_type',
      headerName: t('salary:bonuses.columns.type'),
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const type = params.value || 'other';
        const typeMap = {
          'performance': t('salary:bonuses.types.performance'),
          'annual': t('salary:bonuses.types.annual'),
          'project': t('salary:bonuses.types.project'),
          'referral': t('salary:bonuses.types.referral'),
          'other': t('salary:bonuses.types.other')
        };
        return typeMap[type] || type;
      }
    },
    {
      field: 'amount',
      headerName: t('salary:bonuses.columns.amount'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const currency = params.row.currency || 'NGN';
        return `${currency} ${parseFloat(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'payment_date',
      headerName: t('salary:bonuses.columns.paymentDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'status',
      headerName: t('salary:bonuses.columns.status'),
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'pending';
        const colorMap = {
          'pending': 'warning',
          'approved_by_manager': 'info',
          'approved_by_hr': 'info',
          'approved_by_finance': 'info',
          'approved_by_gm': 'success',
          'paid': 'success',
          'cancelled': 'error'
        };
        const labelMap = {
          'pending': t('salary:bonuses.status.pending'),
          'approved_by_manager': t('salary:bonuses.status.approvedByManager'),
          'approved_by_hr': t('salary:bonuses.status.approvedByHR'),
          'approved_by_finance': t('salary:bonuses.status.approvedByFinance'),
          'approved_by_gm': t('salary:bonuses.status.approvedByGM'),
          'paid': t('salary:bonuses.status.paid'),
          'cancelled': t('salary:bonuses.status.cancelled')
        };
        return (
          <Chip
            label={labelMap[status] || status}
            color={colorMap[status] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const bonus = params.row;
        const canApproveManager = bonus.status === 'pending';
        const canApproveHR = bonus.status === 'approved_by_manager';
        const canApproveFinance = bonus.status === 'approved_by_hr';
        const canApproveGM = bonus.status === 'approved_by_finance';
        const canMarkPaid = bonus.status === 'approved_by_gm';
        const canCancel = bonus.status !== 'paid' && bonus.status !== 'cancelled';

        return (
          <IconButton
            size="small"
            onClick={(e) => handleActionMenuOpen(e, bonus.id)}
            disabled={!canApproveManager && !canApproveHR && !canApproveFinance && !canApproveGM && !canMarkPaid && !canCancel}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        );
      }
    }
  ], [t]);

  if (loading && bonuses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedBonus = bonuses.find(b => b.id === actionMenu.bonusId);
  const canApproveManager = selectedBonus?.status === 'pending';
  const canApproveHR = selectedBonus?.status === 'approved_by_manager';
  const canApproveFinance = selectedBonus?.status === 'approved_by_hr';
  const canApproveGM = selectedBonus?.status === 'approved_by_finance';
  const canMarkPaid = selectedBonus?.status === 'approved_by_gm';
  const canCancel = selectedBonus?.status !== 'paid' && selectedBonus?.status !== 'cancelled';

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('salary:bonuses.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('salary:bonuses.addBonus')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by employee..."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Employee"
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder="Employee ID"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('salary:bonuses.columns.status')}
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('salary:common.all')}</option>
                <option value="pending">{t('salary:bonuses.status.pending')}</option>
                <option value="approved_by_manager">{t('salary:bonuses.status.approvedByManager')}</option>
                <option value="approved_by_hr">{t('salary:bonuses.status.approvedByHR')}</option>
                <option value="approved_by_finance">{t('salary:bonuses.status.approvedByFinance')}</option>
                <option value="approved_by_gm">{t('salary:bonuses.status.approvedByGM')}</option>
                <option value="paid">{t('salary:bonuses.status.paid')}</option>
                <option value="cancelled">{t('salary:bonuses.status.cancelled')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Bonus Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('common:buttons.edit') + ' ' + t('salary:bonuses.title') : t('salary:bonuses.addBonus')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.employee_id || ''})`}
                  value={employees.find(e => e.id === form.employee) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('employee', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      required
                      placeholder="Select employee"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:bonuses.columns.type')}
                  value={form.bonus_type}
                  onChange={(e) => handleFormChange('bonus_type', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="performance">{t('salary:bonuses.types.performance')}</option>
                  <option value="annual">{t('salary:bonuses.types.annual')}</option>
                  <option value="project">{t('salary:bonuses.types.project')}</option>
                  <option value="referral">{t('salary:bonuses.types.referral')}</option>
                  <option value="other">{t('salary:bonuses.types.other')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('salary:bonuses.columns.currency')}
                  value={form.currency}
                  onChange={(e) => handleFormChange('currency', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="NGN">NGN - Nigerian Naira</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                  <option value="USD">USD - US Dollar</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:bonuses.columns.amount')}
                  type="number"
                  value={form.amount}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('salary:bonuses.columns.paymentDate')}
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => handleFormChange('payment_date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  required
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="Taxable"
                  value={form.is_taxable ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_taxable', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="true">{t('employees:leaveTypes.yes')}</option>
                  <option value="false">{t('employees:leaveTypes.no')}</option>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={handleActionMenuClose}
      >
        {canApproveManager && (
          <MenuItem onClick={() => handleApprove('manager')}>Approve (Manager)</MenuItem>
        )}
        {canApproveHR && (
          <MenuItem onClick={() => handleApprove('hr')}>Approve (HR)</MenuItem>
        )}
        {canApproveFinance && (
          <MenuItem onClick={() => handleApprove('finance')}>Approve (Finance)</MenuItem>
        )}
        {canApproveGM && (
          <MenuItem onClick={() => handleApprove('gm')}>Approve (GM)</MenuItem>
        )}
        {canMarkPaid && (
          <MenuItem onClick={() => handleApprove('paid')}>Mark as Paid</MenuItem>
        )}
        {canCancel && (
          <MenuItem onClick={() => handleApprove('cancel')} sx={{ color: 'error.main' }}>Cancel</MenuItem>
        )}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Bonuses;

